import React, { useRef } from 'react';
import { Calendar } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// DatePicker — wrapper sur <input type="date"> avec UI cohérente dark theme
// ─────────────────────────────────────────────────────────────────────────────
// Pourquoi ne pas réinventer un calendrier custom :
//   - L'input natif HTML5 ouvre déjà un calendrier popup au clic
//   - Le calendrier natif est accessible (clavier, lecteurs d'écran, mobile)
//   - On bénéficie automatiquement des dates localisées (fr-FR sur ton Mac)
//
// Ce wrapper apporte :
//   - Style cohérent avec le reste de l'app (dark theme, bordure jaune au focus)
//   - Icône calendrier cliquable bien visible (le picker s'ouvre)
//   - Format de date FR (DD/MM/YYYY) affiché en lecture
//   - Prop `light` pour les rares pages claires (compat futur)
// ─────────────────────────────────────────────────────────────────────────────

// Formate une date ISO (YYYY-MM-DD) au format FR (JJ/MM/AAAA)
function formaterFR(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
}

export default function DatePicker({
  label,
  value,
  onChange,
  min,
  max,
  required = false,
  disabled = false,
  className = '',
  ...rest
}) {
  const inputRef = useRef(null);

  // Ouvrir le picker natif au clic sur l'icône (sinon il faut cliquer pile sur
  // la petite flèche, peu pratique). showPicker() est supporté Chrome/Edge/Safari.
  const ouvrirPicker = () => {
    if (disabled) return;
    inputRef.current?.focus();
    if (inputRef.current?.showPicker) {
      try { inputRef.current.showPicker(); } catch { /* navigateur ancien */ }
    }
  };

  return (
    <div className="space-y-1">
      {label && (
        <label className="block text-xs text-gray-400">
          {label}{required && <span className="text-yellow-500"> *</span>}
        </label>
      )}
      <div
        onClick={ouvrirPicker}
        className={`relative flex items-center bg-gray-800 border border-gray-700 rounded-lg
                    focus-within:border-yellow-500 transition-colors cursor-pointer
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
      >
        <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-yellow-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="date"
          value={value || ''}
          onChange={onChange}
          min={min}
          max={max}
          disabled={disabled}
          required={required}
          className="w-full bg-transparent text-white pl-9 pr-3 py-2.5 text-sm focus:outline-none cursor-pointer
                     [&::-webkit-calendar-picker-indicator]:opacity-0
                     [&::-webkit-calendar-picker-indicator]:absolute
                     [&::-webkit-calendar-picker-indicator]:inset-0
                     [&::-webkit-calendar-picker-indicator]:w-full
                     [&::-webkit-calendar-picker-indicator]:cursor-pointer"
          {...rest}
        />
      </div>
      {/* Aperçu lisible FR sous le champ (rappel pour l'utilisateur) */}
      {value && (
        <p className="text-[11px] text-gray-500 ml-1">📅 {formaterFR(value)}</p>
      )}
    </div>
  );
}
